import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Instagram, Facebook, Linkedin, MessageCircle, CheckCircle2 } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { supabase } from "@/integrations/supabase/client";
import { SOCIAL_LINKS } from "@/lib/constants";
import { FLAGS } from "@/lib/featureFlags";

export default function Footer() {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const isRtl = i18n.language === "he";
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "done" | "error">("idle");

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim() || !formEmail.trim()) return;
    setFormStatus("sending");
    try {
      const { error } = await supabase.from("leads").insert({
        first_name: formName.split(" ")[0],
        last_name: formName.split(" ").slice(1).join(" ") || "",
        email: formEmail,
        source: "footer_form",
        status: "new",
      });
      if (error) throw error;
      setFormStatus("done");
    } catch {
      setFormStatus("error");
    }
  };
  const hue = 235;
  const logoFilter = theme === "light"
    ? `brightness(0) saturate(100%) invert(20%) sepia(20%) saturate(500%) hue-rotate(${hue}deg) brightness(70%)`
    : `brightness(0) saturate(100%) invert(85%) sepia(10%) saturate(400%) hue-rotate(${hue}deg) brightness(110%)`;

  const socialLinks = [
    { icon: <Instagram    size={20} />, href: SOCIAL_LINKS.instagram, label: "Instagram" },
    { icon: <Facebook     size={20} />, href: SOCIAL_LINKS.facebook,  label: "Facebook"  },
    { icon: <Linkedin     size={20} />, href: SOCIAL_LINKS.linkedin,  label: "LinkedIn"  },
    { icon: <MessageCircle size={20} />, href: SOCIAL_LINKS.whatsapp, label: "WhatsApp"  },
  ].filter(s => s.href);

  const cols = [
    {
      title: t("dyna.footer.col1"),
      links: [
        { label: t("dyna.footer.services"), href: "/services" },
        { label: t("dyna.footer.process"), href: "/services" },
        { label: t("dyna.footer.pricing"), href: "/packages" },
      ],
    },
    {
      title: t("dyna.footer.col2"),
      links: [
        { label: t("dyna.footer.about"), href: "/about" },
        ...(FLAGS.showWorkSection
          ? [{ label: t("dyna.footer.work"), href: "/work" }]
          : []),
        { label: t("dyna.footer.blog"), href: "/blog" },
        { label: t("dyna.footer.careers"), href: "/book" },
      ],
    },
    {
      title: t("dyna.footer.col3"),
      links: [
        { label: "hello@dyna-agency.com", href: "mailto:hello@dyna-agency.com" },
        { label: t("dyna.footer.brief"), href: "/book" },
        { label: t("dyna.footer.book"), href: SOCIAL_LINKS.whatsapp, external: true },
      ],
    },
  ];

  const linkStyle: React.CSSProperties = {
    display: "block",
    fontSize: 14,
    color: "var(--dyna-fg-muted)",
    textDecoration: "none",
    marginBottom: 10,
    fontFamily: "Geist, system-ui, sans-serif",
    fontWeight: 400,
    transition: "color 150ms",
  };

  return (
    <footer style={{
      margin: "0 var(--site-px) 40px",
      borderRadius: 16,
      overflow: "hidden",
      border: "1px solid var(--dyna-border)",
      background: "var(--dyna-bg-elevated)",
    }}>
      {/* Contact form — horizontal bar at top */}
      <div style={{
        padding: "20px clamp(28px, 4vw, 48px)",
        borderBottom: "1px solid var(--dyna-border)",
      }}>
        {formStatus === "done" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--dyna-fg-muted)", fontSize: 14, fontFamily: "Geist, system-ui, sans-serif" }}>
            <CheckCircle2 style={{ width: 20, height: 20, color: "var(--dyna-brand)", flexShrink: 0 }} strokeWidth={1.5} />
            <span>{t("footer.contactForm.success")}</span>
          </div>
        ) : (
          <form onSubmit={handleContactSubmit} className="dyna-footer-contact" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }} dir={isRtl ? "rtl" : "ltr"}>
            <span className="dyna-footer-contact__label" style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              letterSpacing: "0.16em", textTransform: "uppercase",
              color: "var(--dyna-fg-muted)", flexShrink: 0,
            }}>
              {t("footer.contactForm.heading")}
            </span>
            <input
              className="dyna-footer-contact__input"
              style={{ flex: "1 1 160px", minWidth: 120, maxWidth: 220 }}
              type="text" required value={formName} onChange={e => setFormName(e.target.value)}
              placeholder={t("footer.contactForm.namePlaceholder") as string}
            />
            <input
              className="dyna-footer-contact__input"
              style={{ flex: "1 1 200px", minWidth: 140, maxWidth: 280 }}
              type="email" required value={formEmail} onChange={e => setFormEmail(e.target.value)}
              placeholder={t("footer.contactForm.emailPlaceholder") as string}
            />
            {formStatus === "error" && (
              <span style={{ fontFamily: "Geist, system-ui, sans-serif", fontSize: 12, color: "rgba(220,80,80,1)" }}>
                {t("footer.contactForm.errorMsg")}
              </span>
            )}
            <button
              type="submit"
              disabled={formStatus === "sending"}
              className="dyna-btn dyna-btn--primary"
              style={{ flexShrink: 0, fontSize: 12, padding: "9px 18px", opacity: formStatus === "sending" ? 0.6 : 1 }}
            >
              {formStatus === "sending" ? "…" : t("footer.contactForm.submit")} <span className="dyna-arrow">→</span>
            </button>
          </form>
        )}
      </div>

      {/* Main section: brand + nav */}
      <div style={{
        padding: "clamp(32px, 5vw, 52px) clamp(28px, 4vw, 48px)",
        display: "flex",
        gap: "clamp(32px, 5vw, 64px)",
        justifyContent: "space-between",
        alignItems: "flex-start",
        flexWrap: "wrap",
      }}>
        {/* Brand column */}
        <div style={{ flexShrink: 0, maxWidth: 280 }}>
          <img
            src="/wordmark.webp"
            alt="DYNA"
            width="298"
            height="104"
            style={{ height: 32, width: "auto", filter: logoFilter, marginBottom: 16, transition: "filter 300ms" }}
          />
          <p style={{
            fontSize: 14, lineHeight: 1.65,
            color: "var(--dyna-fg-muted)",
            margin: "0 0 20px",
            fontFamily: "Geist, system-ui, sans-serif",
            fontWeight: 300,
          }}>
            {t("dyna.footer.tagline")}
          </p>
          <div style={{ display: "flex", gap: 14 }}>
            {socialLinks.map(s => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  color: "var(--dyna-fg-muted)",
                  transition: "color 200ms ease",
                  textDecoration: "none",
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "var(--quartz-200)"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = "var(--dyna-fg-muted)"; }}
              >
                {s.icon}
              </a>
            ))}
          </div>
        </div>

        {/* Nav columns */}
        <div style={{ display: "flex", gap: "clamp(28px, 4vw, 56px)", flexWrap: "wrap" }}>
          {cols.map(col => (
            <div key={col.title} style={{ minWidth: 120 }}>
              <div style={{
                fontSize: 14, fontWeight: 600,
                color: "var(--dyna-fg)",
                fontFamily: "Geist, system-ui, sans-serif",
                letterSpacing: "0.01em",
                marginBottom: 16,
              }}>
                {col.title}
              </div>
              {col.links.map(link =>
                link.href.startsWith("/") ? (
                  <Link key={link.label} to={link.href} style={linkStyle}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "var(--dyna-fg)"; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = "var(--dyna-fg-muted)"; }}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a key={link.label} href={link.href} style={linkStyle}
                    target={link.external ? "_blank" : undefined}
                    rel={link.external ? "noopener noreferrer" : undefined}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "var(--dyna-fg)"; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = "var(--dyna-fg-muted)"; }}
                  >
                    {link.label}
                  </a>
                )
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{
        borderTop: "1px solid var(--dyna-border)",
        padding: "16px clamp(28px, 4vw, 48px)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 12,
      }}>
        <span style={{ fontSize: 13, color: "var(--dyna-fg-muted)", fontFamily: "Geist, system-ui, sans-serif" }}>
          {t("dyna.footer.rights")}
        </span>
        <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
          {[
            { label: t("dyna.footer.legal"), to: "/privacy" },
          ].map(item => (
            <Link
              key={item.label}
              to={item.to}
              style={{
                fontSize: 13,
                color: "var(--dyna-fg-muted)",
                textDecoration: "underline",
                textUnderlineOffset: 2,
                fontFamily: "Geist, system-ui, sans-serif",
              }}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </footer>
  );
}
